import { api } from './api';

/**
 * Driver payout-UPI collection (→ RazorpayX bulk payouts). Backed by /admin/payouts/* —
 * see aisdlc/apna-yatri-backend/UPI_PAYOUT_COLLECTION.md. CSV downloads live in exportService.
 */

export interface UpiSummary {
  totalDrivers: number;
  withUpi: number;
  withoutUpi: number;
  /** Drivers whose current VPA is verified against the bank (Razorpay validate/vpa). */
  verifiedUpi?: number;
  coveragePct: number;
  changes7d: number;
  cachedAt?: string;
}

export interface DriverUpiRow {
  id: number;
  userUid: string;
  status: string;
  approvedAt?: string | null;
  payoutUpiVpa: string | null;
  payoutUpiUpdatedAt: string | null;
  /** Bank-registered holder name + when the current VPA was verified (null = unverified). */
  payoutUpiHolderName?: string | null;
  payoutUpiVerifiedAt?: string | null;
  /** Set when the current VPA was checked and does NOT exist — payouts to it will bounce. */
  payoutUpiVerifyFailedAt?: string | null;
  rewardCoins?: number;
  user?: {
    id: number;
    name?: string | null;
    phone?: string | null;
    email?: string | null;
    currentCity?: string | null;
    homeCity?: string | null;
  };
}

export interface UpiChangeLogRow {
  id: number;
  userUid: string;
  oldVpa: string | null;
  newVpa: string | null;
  source: string; // 'driver_app' | 'admin'
  actorId: number | null;
  ip: string | null;
  createdAt: string;
}

export interface PayoutSettings {
  collectionEnabled: boolean;
  /** UPI_VERIFY_ENABLED: when false, VPA verification is off and the app hides the verify UI. */
  verifyEnabled: boolean;
  razorpayAccountNumber: string;
  razorpayAccountConfigured: boolean;
  defaultAmount: number;
  amountUnit: 'rupees' | 'paise';
  bannerUrl: string; // bonus-push big image
  iconUrl: string; // bonus-push small image (largeIcon)
}

export interface PayoutSettingsPatch {
  collectionEnabled?: boolean;
  verifyEnabled?: boolean;
  razorpayAccountNumber?: string;
  defaultAmount?: number;
  amountUnit?: 'rupees' | 'paise';
  bannerUrl?: string;
  iconUrl?: string;
}

/** Upload a bonus-push image (banner | icon) → returns its S3 public URL for preview, then persist via updatePayoutSettings. */
export const uploadPayoutImage = (kind: 'banner' | 'icon', file: File) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('kind', kind);
  return api.post<{ imageUrl: string; kind: string }>('/admin/payouts/upload-image', formData);
};

/** Page settings (all AppConfig-backed): kill switch, RazorpayX account, default amount + unit. */
export const getPayoutSettings = () => api.get<PayoutSettings>('/admin/payouts/settings');

/** Partial update of the payout settings (needs config:write). */
export const updatePayoutSettings = (body: PayoutSettingsPatch) =>
  api.patch('/admin/payouts/settings', body);

/** Shared current/home-city filter (independent, combinable). */
export interface UpiCityFilter {
  currentCity?: string;
  homeCity?: string;
}

/** Distinct driver cities (current ∪ home) for the filter dropdowns. */
export const getPayoutCities = () => api.get<{ cities: string[] }>('/admin/payouts/cities');

export const getUpiSummary = (params?: UpiCityFilter) =>
  api.get<UpiSummary>('/admin/payouts/upi-summary', { params });

export const getDriverUpiList = (params?: {
  cursor?: number;
  limit?: number;
  search?: string;
  hasUpi?: 'yes' | 'no';
} & UpiCityFilter) => api.get('/admin/payouts/upi-list', { params });

/** Set/correct a driver's payout VPA (empty string clears it). Verified against Razorpay server-side
 * (non-existent VPAs are rejected with a 400); journaled with the actor. */
export const setDriverUpi = (driverId: number, upiVpa: string) =>
  api.patch<{
    message: string;
    upiVpa: string | null;
    updatedAt: string | null;
    holderName?: string | null;
    verified?: boolean;
  }>(`/admin/payouts/driver/${driverId}/upi`, { upiVpa });

export const getDriverUpiHistory = (driverId: number) =>
  api.get<{ data: UpiChangeLogRow[] }>(`/admin/payouts/driver/${driverId}/upi-history`);

export interface UpiBackfillSummary {
  message: string;
  checked: number;
  verified: number;
  invalid: number;
  unavailable: number;
  remaining: number;
}

/** Verify pre-existing (unverified) driver UPIs against Razorpay, ~200 per run; rerun while remaining > 0. */
export const runUpiVerifyBackfill = (limit?: number) =>
  api.post<UpiBackfillSummary>('/admin/payouts/verify-backfill', limit ? { limit } : {});

// ---- Reward coins + payout batches ----

export interface CoinTxRow {
  id: number;
  userUid: string;
  delta: number;
  reason: string; // admin_grant | admin_revoke | payout | adjust
  balanceAfter: number;
  batchId: number | null;
  note: string | null;
  actorId: number | null;
  createdAt: string;
}

export interface PayoutBatchRow {
  id: number;
  status: string; // draft | processing | paid | cancelled
  amountPerDriver: number;
  note: string | null;
  filterCurrentCity: string | null;
  filterHomeCity: string | null;
  totalDrivers: number;
  totalCoins: number;
  processedDrivers?: number; // items processed so far (worker progress)
  coinsPaid?: number; // running total debited
  createdById: number | null;
  createdAt: string;
  startedAt?: string | null;
  paidAt: string | null;
}

/** mark-paid returns either an inline run tally, or (large batches) a queued-to-worker ack. */
export interface MarkPaidResult {
  message: string;
  queued: boolean;
  paid?: number;
  skipped?: number;
  coinsDebited?: number;
  notified?: number;
  batchId?: number;
  totalDrivers?: number;
}

export interface PayoutBatchItemRow {
  id: number;
  batchId: number;
  driverId: number;
  userUid: string;
  upiVpa: string;
  coins: number;
  status: string; // pending | paid | failed | skipped
  paidAt: string | null;
  failureReason: string | null;
}

/** Grant (positive) or revoke (negative, single driver only) coins. driverId → single; else bulk by city. */
export const grantCoins = (body: {
  driverId?: number;
  amount: number;
  note?: string;
  currentCity?: string;
  homeCity?: string;
}) => api.post('/admin/coins/grant', body);

export const getDriverCoinHistory = (driverId: number) =>
  api.get<{ data: CoinTxRow[] }>(`/admin/coins/driver/${driverId}/history`);

export const createPayoutBatch = (body: { amount: number; currentCity?: string; homeCity?: string; note?: string }) =>
  api.post<{ message: string; batch: PayoutBatchRow }>('/admin/payouts/batches', body);

export interface PayoutBatchListParams {
  limit?: number;
  cursor?: number;
  status?: string; // draft | processing | paid | cancelled
  from?: string; // YYYY-MM-DD (createdAt >=)
  to?: string; // YYYY-MM-DD (createdAt <=, end of day)
}

export const listPayoutBatches = (params?: PayoutBatchListParams) =>
  api.get<{
    data: PayoutBatchRow[];
    meta: { nextCursor: number | null; hasNextPage: boolean; limit: number };
  }>('/admin/payouts/batches', { params });

export const getPayoutBatch = (id: number) =>
  api.get<PayoutBatchRow & { items: PayoutBatchItemRow[] }>(`/admin/payouts/batch/${id}`);

export const markPayoutBatchPaid = (
  id: number,
  body?: { itemIds?: number[]; failed?: { itemId: number; reason?: string }[] },
) => api.post<MarkPaidResult>(`/admin/payouts/batch/${id}/mark-paid`, body ?? {});

export const cancelPayoutBatch = (id: number) => api.post(`/admin/payouts/batch/${id}/cancel`, {});
